<template>
  <div class="p-grid">
    <div class="p-col-12">
      <div class="card card-w-title">
        <div class="contact-page-header">
          <Button icon="pi pi-arrow-left" class="contact-page-back-button" @click="backToList" />
          <div class="contact-page-title">{{ title }}</div>
        </div>

        <ContactDatatable
          :key="componentKey"
          :model="dataModel"
          :postId="$route.query.id"
          :getFilterOptions="getFilterOptions"
          :actions="getRowActions()"
          @loaded="onLoaded"
          @action-click="onActionClicked"
        />
      </div>
    </div>

    <Dialog
      :header="contactForm.id ? 'Edit contact' : 'Create contact'"
      :visible.sync="showContactDialog"
      :style="{width: '650px'}"
      :modal="true"
      :contentStyle="{ overflow: 'visible' }"
    >
      <div class="p-fluid contact-dialog-form">
        <div class="p-field-checkbox mb-3">
          <Checkbox v-model="contactForm.is_member" :binary="true" @change="changeMemberType" />
          <label class="ml-2">Five2one member</label>
        </div>

        <div v-if="contactForm.is_member">
          <div class="p-field">
            <label>User</label>
            <Dropdown
              v-model="contactForm.uid"
              :options="listUser"
              optionLabel="display_name"
              optionValue="id"
              placeholder="Choose"
              appendTo="self"
              class="contact-user-dropdown"
              panelClass="contact-user-dropdown-panel"
              :autoZIndex="true"
              :baseZIndex="12000"
              :filter="true"
              filterBy="display_name,search_text"
              @change="changeUser"
            />
          </div>
        </div>
        <div v-else>
          <div class="p-field">
            <label>Fullname</label>
            <InputText
              v-model="contactForm.fullname"
              placeholder="Input"
              :class="{ 'p-invalid': showRequiredError('fullname') }"
            />
            <small v-if="showRequiredError('fullname')" class="p-error">Fullname is required</small>
          </div>
        </div>

        <div class="row">
          <div class="col-6">
            <div class="p-field">
              <label>Phone</label>
              <InputText
                v-model="contactForm.phone"
                placeholder="Input"
                :disabled="contactForm.is_member"
                :class="{ 'p-invalid': showRequiredError('phone') }"
                @input="sanitizePhoneInput"
              />
              <small v-if="showRequiredError('phone')" class="p-error">Phone is required</small>
            </div>
          </div>
          <div class="col-6">
            <div class="p-field">
              <label>Email</label>
              <InputText
                v-model="contactForm.email"
                placeholder="Input"
                :disabled="contactForm.is_member"
                :class="{ 'p-invalid': showRequiredError('email') || !isEmailValid }"
              />
              <small v-if="showRequiredError('email')" class="p-error">Email is required</small>
              <small v-if="!isEmailValid" class="p-error">Invalid email format</small>
            </div>
          </div>
        </div>

        <div class="p-field">
          <label>Contact date</label>
          <Calendar
            v-model="contactForm.contact_date"
            :disabled="true"
            :showOnFocus="false"
            :showIcon="true"
            appendTo="body"
            dateFormat="MM dd, yy"
            showTime
            hourFormat="24"
            placeholder="Choose"
            :manualInput="false"
          />
        </div>

        <div class="p-field">
          <label>Note</label>
          <Textarea v-model="contactForm.note" rows="4" placeholder="Input" />
        </div>
      </div>
      <template #footer>
        <Button label="Cancel" @click="showContactDialog = false" class="contact-dialog-cancel-button" />
        <Button label="Save" @click="saveContact" class="main-style-button" />
      </template>
    </Dialog>
  </div>
</template>

<script>
import moment from "moment-timezone";
import DataServices from '@/core/DataServices'
import ApiRepository from "@/core/ApiRepository";
import ContactDatatable from './contact_list'

const isBlankValue = (value) => value === null || value === undefined || `${value}`.trim() === '';

export default {
  components: {
    ContactDatatable
  },
  data() {
    return {
      dataModel: null,
      title: 'Number of contacts',
      componentKey: 0,
      post: null,
      listUser: [],
      mapUser: {},
      showContactDialog: false,
      contactForm: {},
      submitted: false,
      page_transaction: null,
    }
  },
  computed: {
    isEmailValid() {
      const email = (this.contactForm?.email ?? '').trim();
      if (email === '') {
        return true;
      }
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    },
    isEditingContact() {
      return !this.$commonFuction.isEmpty(this.contactForm?.id);
    },
    isGuestContactMissingRequired() {
      if (this.contactForm?.is_member === true) {
        return false;
      }
      return ['fullname', 'phone', 'email'].some(key => isBlankValue(this.contactForm?.[key]));
    },
  },
  async created() {
    this.dataModel = DataServices.getModel('property_for_sale_contact');
    if (!this.$commonFuction.isEmpty(this.$route.query?.page_transaction)) {
      this.page_transaction = this.$route.query.page_transaction;
    }
    await this.loadDetail();
  },
  methods: {
    async loadDetail() {
      try {
        const headers = {
          Authorization: "Bearer " + (await this.$AuthenticationApp.getToken()),
        };
        const response = await ApiRepository.post(
          "/_api/property/detail_property_for_sale_admin",
          { id: this.$route.query.id },
          { headers }
        );
        const responseData = response?.data?.data ?? response?.data ?? {};
        this.post = responseData?.post ?? null;
        this.listUser = (responseData?.list_user ?? []).map(item => {
          const phoneFull = item?.phone_full ?? `${item?.prefix_phone ?? ''}${item?.phone ?? ''}`;
          return {
            ...item,
            phone_full: phoneFull,
            display_name: item?.display_name ?? [item?.fullname ?? '', item?.email ?? '', phoneFull]
              .filter(m => !isBlankValue(m))
              .join(' - '),
            search_text: item?.search_text ?? [item?.fullname ?? '', item?.email ?? '', phoneFull]
              .filter(m => !isBlankValue(m))
              .join(' ')
          };
        });
        this.mapUser = {};
        this.listUser.forEach(item => {
          this.mapUser[item.id] = item;
        });
        if (this.post) {
          this.title = `${this.post.property_name || ''} - Number of contacts`;
        }
      } catch (err) {
        const error = err?.response?.data ?? null;
        this.$toast.add({
          severity: "error",
          summary: "Error",
          detail: `Exception error [${error?.message || "LOAD_PROPERTY_FOR_SALE_CONTACT_FAILED"}]`,
          life: 3000,
        });
      }
    },
    onLoaded(data) {
      if (data?.post && !this.post) {
        this.post = data.post;
        this.title = `${data.post.property_name || ''} - Number of contacts`;
      }
    },
    getFilterOptions() {
      return [];
    },
    getRowActions() {
      return {
        header: { name: 'add', icon: 'plus' },
        rows() {
          return {
            edit: { name: 'edit', icon: 'pencil' }
          };
        }
      };
    },
    defaultContactForm() {
      const contactDate = new Date();
      contactDate.setSeconds(0, 0);
      return {
        id: null,
        post_id: this.$route.query.id,
        is_member: false,
        uid: null,
        fullname: '',
        phone: '',
        email: '',
        contact_date: contactDate,
        note: '',
      };
    },
    openContactDialog(item = null) {
      this.submitted = false;
      this.contactForm = this.defaultContactForm();
      if (item) {
        this.contactForm = {
          id: item.id,
          post_id: item.post_id ?? this.$route.query.id,
          is_member: item.is_member === true,
          uid: item.uid ?? null,
          fullname: item.fullname_display ?? item.fullname ?? '',
          phone: item.phone_display ?? item.phone ?? '',
          email: item.email_display ?? item.email ?? '',
          contact_date: item.contact_date ? new Date(item.contact_date) : new Date(),
          note: item.note ?? '',
        };
      }
      this.showContactDialog = true;
    },
    changeMemberType() {
      this.submitted = false;
      this.contactForm.uid = null;
      this.contactForm.fullname = '';
      this.contactForm.phone = '';
      this.contactForm.email = '';
    },
    showRequiredError(field) {
      if (this.submitted !== true || this.contactForm?.is_member === true) {
        return false;
      }
      return isBlankValue(this.contactForm?.[field]);
    },
    changeUser() {
      var user = this.mapUser[this.contactForm.uid] ?? {};
      this.contactForm.fullname = user.fullname ?? '';
      this.contactForm.phone = user.phone_full ?? '';
      this.contactForm.email = user.email ?? '';
    },
    sanitizePhoneInput(event) {
      const rawValue = event?.target?.value ?? this.contactForm.phone ?? '';
      this.contactForm.phone = rawValue.replace(/[^0-9+\-()\s]/g, '');
    },
    async saveContact() {
      try {
        this.submitted = true;
        if (this.isGuestContactMissingRequired) {
          this.$toast.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Fullname, phone and email are required',
            life: 3000
          });
          return;
        }
        if (!this.isEmailValid) {
          this.$toast.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Email is invalid',
            life: 3000
          });
          return;
        }
        const headers = {
          Authorization: "Bearer " + (await this.$AuthenticationApp.getToken()),
        };
        const payload = {
          ...this.contactForm,
          post_id: this.$route.query.id,
        };
        if (this.isEditingContact) {
          delete payload.contact_date;
          delete payload.created_at;
        } else {
          payload.created_at = moment(this.contactForm.contact_date).format('YYYY-MM-DDTHH:mm:ssZ');
          delete payload.contact_date;
        }
        await ApiRepository.post('/_api/property/save_property_for_sale_contact_admin', payload, { headers });
        this.$toast.add({ severity: 'success', summary: 'Success', detail: 'Saved contact successfully', life: 3000 });
        this.showContactDialog = false;
        this.componentKey++;
      } catch (err) {
        const error = err?.response?.data ?? null;
        this.$toast.add({ severity: 'error', summary: 'Error', detail: `Exception error [${error?.message || 'SAVE_CONTACT_FAILED'}]`, life: 3000 });
      }
    },
    onActionClicked(event) {
      if (event?.action?.name === 'add') {
        this.openContactDialog();
        return;
      }
      if (event?.action?.name === 'edit') {
        this.openContactDialog(event?.data?.data ?? null);
      }
    },
    backToList() {
      const router_back = { path: "/admin/list-property_for_sale" };
      if (!this.$commonFuction.isEmpty(this.page_transaction)) {
        router_back.query = { page_transaction: this.page_transaction.toString() };
      }
      this.$router.push(router_back);
    },
  }
}
</script>

<style lang="scss">
.contact-page-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding-bottom: 18px;
}

.contact-page-title {
  font-size: 18px;
  font-weight: 700;
  color: #243043;
  line-height: 1.4;
}

.contact-page-back-button.p-button {
  width: 30px !important;
  height: 30px !important;
  min-width: 30px !important;
  border-radius: 4px !important;
  background: unset !important;
  border: unset !important;
  color: #8f99ab !important;
  box-shadow: none !important;
  padding: 0 !important;
}

.contact-page-back-button.p-button:hover,
.contact-page-back-button.p-button:focus,
.contact-page-back-button.p-button:enabled:hover {
  background: #e9edf3 !important;
  border-color: #e9edf3 !important;
  color: #6d788a !important;
}

.contact-page-back-button.p-button .p-button-icon,
.contact-page-back-button.p-button .pi {
  font-size: 0.85rem;
  font-weight: 700;
  color: inherit !important;
}

.contact-page-back-button.p-button .p-button-label {
  display: none;
}

.contact-dialog-cancel-button.p-button {
  min-width: 66px;
  justify-content: center;
  background: #f3f4f7 !important;
  border: 1px solid #f3f4f7 !important;
  color: #6b7280 !important;
}

.contact-dialog-cancel-button.p-button .p-button-label {
  color: #6b7280 !important;
  text-align: center;
  width: 100%;
}

.contact-dialog-cancel-button.p-button:hover,
.contact-dialog-cancel-button.p-button:focus,
.contact-dialog-cancel-button.p-button:enabled:hover {
  background: #e9edf3 !important;
  border-color: #e9edf3 !important;
  color: #4b5563 !important;
}

.contact-dialog-cancel-button.p-button:hover .p-button-label,
.contact-dialog-cancel-button.p-button:focus .p-button-label,
.contact-dialog-cancel-button.p-button:enabled:hover .p-button-label {
  color: #4b5563 !important;
}

.contact-dialog-form :deep(.contact-user-dropdown) {
  width: 100%;
}

.contact-user-dropdown-panel {
  width: 480px !important;
  max-width: calc(100vw - 48px) !important;
  z-index: 12000 !important;
}
</style>
